import { useEffect, useState } from 'react'
import { BarChart2 } from 'lucide-react'
import { apiGet } from '../lib/api'

interface UsageSummary {
  total_tokens: number
  estimated_cost_usd: number
}

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000)     return `${(n / 1_000).toFixed(0)}K`
  return String(n)
}

/**
 * Renders a single-line token + cost summary for the current calendar month.
 * Only fetches and renders when the user is an admin — returns null otherwise.
 */
export default function UsageBadge({ isAdmin }: { isAdmin: boolean }) {
  const [summary, setSummary] = useState<UsageSummary | null>(null)

  useEffect(() => {
    if (!isAdmin) return
    apiGet('/api/usage/summary')
      .then((data: any) => {
        if (data?.total_tokens !== undefined) {
          setSummary({
            total_tokens:       data.total_tokens,
            estimated_cost_usd: data.estimated_cost_usd ?? 0,
          })
        }
      })
      .catch(() => { /* non-fatal — badge stays hidden on error */ })
  }, [isAdmin])

  if (!isAdmin || !summary) return null

  return (
    <div style={{
      display:        'flex',
      alignItems:     'center',
      gap:            6,
      padding:        '5px 10px',
      fontSize:       11,
      color:          'var(--text-muted, #6b7280)',
      borderBottom:   '1px solid var(--border)',
      marginBottom:   4,
    }}>
      <BarChart2 size={11} style={{ flexShrink: 0 }} />
      <span>
        This month:&nbsp;
        <span style={{ color: 'var(--text-secondary, #9ca3af)', fontVariantNumeric: 'tabular-nums' }}>
          {formatTokens(summary.total_tokens)} tokens
        </span>
        &nbsp;·&nbsp;
        <span style={{ color: 'var(--text-secondary, #9ca3af)', fontVariantNumeric: 'tabular-nums' }}>
          ~${summary.estimated_cost_usd.toFixed(2)}
        </span>
      </span>
    </div>
  )
}
